import { useEffect, useRef, useState } from 'react';
import { useSendVerification } from '@/hooks/queries/useAuthQueries';
import { getBorrowerSignupData, getInvestorSignupData, useSignupStore } from '@/stores/signupStore';
import { UserFormTypeRole } from '@/utils/user';
import EULADocument from '../legal/EULADocument';
import { SignupStepLayout } from './SignupStepLayout';

type TermsStepProps = {
  onBack: () => void;
  onNext: () => void;
};

export default function TermsStep({ onBack, onNext }: TermsStepProps) {
  const signupStore = useSignupStore();
  const [readTerms, setReadTerms] = useState(false);
  const tosRef = useRef<HTMLDivElement | null>(null);
  const selectedRole = signupStore.role;
  const sendVerificationMutation = useSendVerification();
  const isBorrower = selectedRole === UserFormTypeRole.BORROWER;

  const isSubmitting = sendVerificationMutation.isPending;

  const handleSubmit = async () => {
    try {
      const userData = isBorrower
        ? getBorrowerSignupData(signupStore.borrowerForm.formData)
        : getInvestorSignupData(signupStore.investorForm.formData);

      await sendVerificationMutation.mutateAsync(userData);

      onNext();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Network error';

      if (isBorrower) {
        signupStore.setBorrowerErrors({ general: [errorMessage] });
      } else {
        signupStore.setInvestorErrors({ general: [errorMessage] });
      }

      onBack();
    }
  };

  useEffect(() => {
    const el = tosRef.current;
    if (!el) return;

    const check = () => {
      const isScrollable = el.scrollHeight > el.clientHeight;
      const atBottom = Math.abs(el.scrollHeight - el.clientHeight - el.scrollTop) <= 2;

      if (isScrollable) {
        if (atBottom) {
          setReadTerms(true);
        }
        return;
      }
    };

    const onScroll = () => check();
    el.addEventListener('scroll', onScroll);

    // watch for content resize (markdown load may change size)
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => check());
      ro.observe(el);
    } else {
      window.addEventListener('resize', check);
    }

    // initial check after content load
    const t = setTimeout(check, 100);

    return () => {
      el.removeEventListener('scroll', onScroll);
      clearTimeout(t);
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', check);
    };
  }, []);

  return (
    <SignupStepLayout
      actions={{
        primaryLabel: 'Agree and Sign Up',
        onPrimaryClick: handleSubmit,
        onBack,
        primaryDisabled: !readTerms,
        primaryLoading: isSubmitting,
        backDisabled: isSubmitting,
      }}
    >
      <div className="w-full flex flex-col gap-6 items-center">
        <div className="w-full max-w-[720px]">
          <h2 className="text-center text-black text-2xl font-medium break-words mb-2">Terms of Service</h2>
          <div className="text-sm text-gray-600 text-center mb-4">Please read the Terms of Service below.</div>

          <div className="w-full rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div
              ref={tosRef}
              role="region"
              aria-label="Terms of Service"
              className="w-full h-64 sm:h-80 md:h-96 overflow-y-auto p-6 text-sm text-gray-700"
            >
              <div className="prose prose-sm prose-gray max-w-none">
                <EULADocument />
              </div>
            </div>
          </div>
        </div>
      </div>
    </SignupStepLayout>
  );
}
